import React, { useCallback, useEffect, useState } from "react";
import { MapPin, Loader2, Plus, Power, Pencil } from "lucide-react";

import { Card } from "src/components/ui/card";
import { Button } from "src/components/ui/button";
import { Input } from "src/components/ui/input";
import { Label } from "src/components/ui/label";
import { useToast } from "src/components/ui/toast";
import { useAuth } from "src/contexts/AuthContext";
import { GeofenceMap } from "src/features/attendance/components/GeofenceMap";
import {
  fetchProjectGeofences,
  createProjectGeofence,
  updateProjectGeofence,
  deactivateProjectGeofence,
} from "src/features/site_visits/api/siteVisitGeofencesApi";

const DEFAULT_CENTER = { latitude: 19.076, longitude: 72.8777 }; // Mumbai

function blankDraft(project) {
  return {
    name: project?.name ? `${project.name} — Site` : "Site Visit Location",
    address: project?.address || "",
    latitude: project?.latitude || DEFAULT_CENTER.latitude,
    longitude: project?.longitude || DEFAULT_CENTER.longitude,
    radius_meters: 150,
    active: true,
  };
}

// Per-project site-visit geofence: the circle a rep must stand inside to record
// a verified check-in. Lives on the project detail page.
export function ProjectSiteVisitGeofence({ projectId, project, className = "" }) {
  const { toast } = useToast();
  const { can } = useAuth();
  const [fence, setFence] = useState(null);
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);

  const canConfigure = can("site_visits", "configure");

  const load = useCallback(() => {
    setLoading(true);
    fetchProjectGeofences(projectId)
      .then((list) => setFence(list[0] || null))
      .catch(() => toast({ variant: "destructive", title: "Couldn't load site location" }))
      .finally(() => setLoading(false));
  }, [projectId, toast]);

  useEffect(() => {
    load();
  }, [load]);

  function startEdit() {
    setDraft(fence ? { ...fence } : blankDraft(project));
  }

  function setCoords(lat, lng) {
    setDraft((d) => ({ ...d, latitude: lat, longitude: lng }));
  }

  async function save(e) {
    e.preventDefault();
    if (!draft.name.trim()) {
      toast({ variant: "destructive", title: "Validation", description: "Name is required." });
      return;
    }
    setSaving(true);
    const payload = {
      name: draft.name.trim(),
      address: draft.address,
      latitude: draft.latitude,
      longitude: draft.longitude,
      radius_meters: Number(draft.radius_meters),
      active: true,
    };
    try {
      if (draft.id) {
        await updateProjectGeofence(draft.id, payload);
      } else {
        await createProjectGeofence(projectId, payload);
      }
      toast({ title: "Saved", description: "Site visit location updated." });
      setDraft(null);
      load();
    } catch (error) {
      const errs = error.response?.data?.errors;
      toast({
        variant: "destructive",
        title: "Couldn't save",
        description: Array.isArray(errs) ? errs.join(", ") : "Something went wrong.",
      });
    } finally {
      setSaving(false);
    }
  }

  async function toggleActive() {
    if (!fence) return;
    try {
      if (fence.active) {
        await deactivateProjectGeofence(fence.id);
      } else {
        await updateProjectGeofence(fence.id, { active: true });
      }
      load();
    } catch {
      toast({ variant: "destructive", title: "Couldn't update" });
    }
  }

  return (
    <Card className={`p-5 ${className}`}>
      <div className="mb-4 flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-slate-100">
            <MapPin className="h-4.5 w-4.5 text-slate-600" />
          </span>
          <div>
            <h3 className="text-sm font-semibold text-slate-800">Site Visit Location</h3>
            <p className="text-[12px] text-slate-400">
              The zone a rep must be inside to record a verified check-in.
            </p>
          </div>
        </div>
        {canConfigure && !draft && (
          <Button size="sm" variant="outline" onClick={startEdit}>
            {fence ? (
              <>
                <Pencil className="mr-1.5 h-3.5 w-3.5" /> Edit
              </>
            ) : (
              <>
                <Plus className="mr-1.5 h-3.5 w-3.5" /> Set location
              </>
            )}
          </Button>
        )}
      </div>

      {loading ? (
        <div className="py-8 text-center text-sm text-slate-400">
          <Loader2 className="mx-auto h-5 w-5 animate-spin" />
        </div>
      ) : draft ? (
        <form onSubmit={save} className="space-y-4">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label>Name</Label>
              <Input
                value={draft.name}
                onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))}
              />
            </div>
            <div className="space-y-1.5">
              <Label>Radius (m)</Label>
              <Input
                type="number"
                min={20}
                max={5000}
                value={draft.radius_meters}
                onChange={(e) => setDraft((d) => ({ ...d, radius_meters: e.target.value }))}
              />
            </div>
          </div>

          <div className="overflow-hidden rounded-lg border border-slate-200">
            <GeofenceMap
              latitude={Number(draft.latitude)}
              longitude={Number(draft.longitude)}
              radiusMeters={Number(draft.radius_meters) || 150}
              onChange={setCoords}
              height={260}
            />
          </div>
          <p className="text-[12px] text-slate-400">
            Click the map or use “locate me” to place the centre · {Number(draft.latitude).toFixed(5)},{" "}
            {Number(draft.longitude).toFixed(5)}
          </p>

          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={() => setDraft(null)} disabled={saving}>
              Cancel
            </Button>
            <Button type="submit" disabled={saving}>
              {saving && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}
              Save location
            </Button>
          </div>
        </form>
      ) : fence ? (
        <div className="space-y-3">
          <div className="overflow-hidden rounded-lg border border-slate-200">
            <GeofenceMap
              latitude={Number(fence.latitude)}
              longitude={Number(fence.longitude)}
              radiusMeters={Number(fence.radius_meters)}
              onChange={() => {}}
              height={220}
            />
          </div>
          <div className="flex items-center justify-between text-[13px]">
            <div className="text-slate-600">
              <span className="font-medium text-slate-800">{fence.name}</span>
              <span className="mx-1.5 text-slate-300">·</span>
              {fence.radius_meters} m radius
              <span className="mx-1.5 text-slate-300">·</span>
              <span className={fence.active ? "text-emerald-600" : "text-rose-500"}>
                {fence.active ? "Active" : "Disabled"}
              </span>
            </div>
            {canConfigure && (
              <Button size="sm" variant="ghost" onClick={toggleActive} className="text-slate-500">
                <Power className="mr-1.5 h-3.5 w-3.5" />
                {fence.active ? "Disable" : "Enable"}
              </Button>
            )}
          </div>
        </div>
      ) : (
        <div className="rounded-xl border border-dashed border-slate-200 py-10 text-center">
          <MapPin className="mx-auto h-7 w-7 text-slate-300" />
          <p className="mt-2 text-sm text-slate-500">No site-visit location set for this project yet.</p>
          {canConfigure && (
            <Button variant="outline" className="mt-3" onClick={startEdit}>
              <Plus className="mr-1.5 h-3.5 w-3.5" /> Set location
            </Button>
          )}
        </div>
      )}
    </Card>
  );
}
